import {TweenLite} from 'gsap';
import {normalizeIndex, range} from '../helpers';

export default class SliderComponent {
  static CLASSES = {
    SLIDER: 'slider',
    SLIDER_ANIMATING: 'slider--animating',
    SLIDER_SINGLE: 'slider--single',
    SLIDER_CAROUSEL: 'slider--carousel',
    SLIDER_INFINITE: 'slider--infinite',
    SLIDER_INITIALIZED: 'slider--initialized',
    LIST: 'slider__list',
    ITEM: 'slider__item',
    ITEM_ACTIVE: 'slider__item--active',
    ITEM_CLONE: 'slider__item--clone',
    BUTTON_PREV: 'slider__button--prev',
    BUTTON_NEXT: 'slider__button--next',
    DOTS: 'slider__dots',
    DOT: 'slider__dot',
    DOT_ACTIVE: 'slider__dot--active',
    PAGE_NUMBER: 'slider__page-number'
  };

  static init($slider) {
    if (!$slider || $slider.cardFortSliderComponent) {
      return;
    }

    $slider.cardFortSliderComponent = new SliderComponent($slider);
  }

  static initAll() {
    document.querySelectorAll(`.${SliderComponent.CLASSES.SLIDER}`).forEach(($slider) => {
      SliderComponent.init($slider);
    });
  }

  constructor($slider) {
    this.$slider = $slider;
    this.$list = $slider.querySelector(`.${SliderComponent.CLASSES.LIST}`);
    this.$$item = $slider.querySelectorAll(`.${SliderComponent.CLASSES.ITEM}`);
    this.$buttonPrev = $slider.querySelector(`.${SliderComponent.CLASSES.BUTTON_PREV}`);
    this.$buttonNext = $slider.querySelector(`.${SliderComponent.CLASSES.BUTTON_NEXT}`);
    this.$dots = $slider.querySelector(`.${SliderComponent.CLASSES.DOTS}`);
    this.$$dot = $slider.querySelectorAll(`.${SliderComponent.CLASSES.DOT}`);
    this.$pageNumber = $slider.querySelector(`.${SliderComponent.CLASSES.PAGE_NUMBER}`);

    this.index = 0;

    this.onResizeWindow = this.onResizeWindow.bind(this);
    this.onClickButtonPrev = this.onClickButtonPrev.bind(this);
    this.onClickButtonNext = this.onClickButtonNext.bind(this);
    this.onClickDots = this.onClickDots.bind(this);

    this.init();
  }

  init() {
    if (this.isCarousel()) {
      if (this.isInfinite()) {
        this.makeClones(4).then(() => {
          this.scrollListToSlide(this.index, {duration: 0});
          window.addEventListener('resize', this.onResizeWindow);
        });
      } else {
        this.scrollListToSlide(this.index, {duration: 0});
        window.addEventListener('resize', this.onResizeWindow);
      }
    }

    this.$buttonPrev.addEventListener('click', this.onClickButtonPrev);
    this.$buttonNext.addEventListener('click', this.onClickButtonNext);

    if (this.$dots) {
      this.$dots.addEventListener('click', this.onClickDots);
    }

    this.$slider.classList.add(SliderComponent.CLASSES.SLIDER_INITIALIZED);
  }

  destroy() {
    if (this.isCarousel()) {
      TweenLite.killTweensOf(this.$list);
      TweenLite.set(this.$list, {clearProps: 'all'});
      this.$list.removeAttribute('style');
      this.setAnimating(false);

      if (this.isInfinite()) {
        this.$list.querySelectorAll(`.${SliderComponent.CLASSES.ITEM_CLONE}`).forEach(($clone) => {
          $clone.remove();
        });
      }

      window.removeEventListener('resize', this.onResizeWindow);
    }

    this.$buttonPrev.removeEventListener('click', this.onClickButtonPrev);
    this.$buttonNext.removeEventListener('click', this.onClickButtonNext);

    if (this.$dots) {
      this.$dots.removeEventListener('click', this.onClickDots);
    }

    this.$slider.classList.remove(SliderComponent.CLASSES.SLIDER_INITIALIZED);
    this.$slider.cardFortSliderComponent = null;
  }

  onResizeWindow() {
    clearTimeout(this.windowResizeTimeoutId);

    this.windowResizeTimeoutId = setTimeout(() => {
      this.scrollListToSlide(this.index);
    }, 300);
  }

  onClickButtonPrev(event) {
    this.prevSlide();
  }

  onClickButtonNext(event) {
    this.nextSlide();
  }

  onClickDots(event) {
    let $target = event.target;

    while ($target !== event.currentTarget) {
      if ($target.classList.contains(SliderComponent.CLASSES.DOT)) {
        this.toSlide(Array.from(this.$$dot).indexOf($target));
        break;
      }

      $target = $target.parentElement;
    }
  }

  isAnimating() {
    return this.$slider.classList.contains(SliderComponent.CLASSES.SLIDER_ANIMATING);
  }

  setAnimating(flag) {
    this.$slider.classList.toggle(SliderComponent.CLASSES.SLIDER_ANIMATING, flag);
  }

  isSingle() {
    return this.$slider.classList.contains(SliderComponent.CLASSES.SLIDER_SINGLE);
  }

  isCarousel() {
    return this.$slider.classList.contains(SliderComponent.CLASSES.SLIDER_CAROUSEL);
  }

  isInfinite() {
    return this.$slider.classList.contains(SliderComponent.CLASSES.SLIDER_INFINITE);
  }

  makeClones(count) {
    return new Promise((resolve) => {
      this.$$item.forEach(($item, index) => {
        $item.dataset.index = index;
      });

      let leftIndexes = range(-count, -1);
      let rightIndexes = range(0, count - 1);

      let $$$clones = [leftIndexes, rightIndexes].map((indexes) => {
        return indexes.map((index) => {
          let $clone = this.$$item[normalizeIndex(index, this.$$item.length)].cloneNode(true);

          $clone.classList.add(SliderComponent.CLASSES.ITEM_CLONE);

          return $clone;
        });
      });

      this.$list.prepend.apply(this.$list, $$$clones[0]);
      this.$list.append.apply(this.$list, $$$clones[1]);

      resolve();
    });
  }

  calcListXForItem($item) {
    let itemLeft = $item.offsetLeft;
    let itemWidth = $item.offsetWidth;

    return -1 * (itemLeft - (window.innerWidth / 2 - itemWidth / 2));
  }

  scrollListToSlide(index, {duration = 0.3, direction = null} = {}) {
    if (innerWidth <= 850) {
      return;
    }

    let isInfinite = this.isInfinite();
    let isFromStart = this.index === 0;
    let isFromEnd = this.index === this.$$item.length - 1;
    let isToStart = index === 0;
    let isToEnd = index === this.$$item.length - 1;
    let isBackFromStartToEnd = isFromStart && isToEnd && direction === 'prev';
    let isForwardFromEndToStart = isFromEnd && isToStart && direction === 'next';
    let $item = this.$$item[index];
    let $itemClone;

    if (isInfinite) {
      if (isBackFromStartToEnd) {
        $itemClone = this.$$item[0].previousElementSibling;
      } else if (isForwardFromEndToStart) {
        $itemClone = this.$$item[this.$$item.length - 1].nextElementSibling;
      }
    }

    if (!$item) {
      return;
    }

    let currentX = this.$list._gsTransform ? this.$list._gsTransform.x : 0;
    let cloneX = $itemClone && this.calcListXForItem($itemClone);
    let x = this.calcListXForItem($item);

    if (x === currentX) {
      return;
    }

    TweenLite.killTweensOf(this.$list);

    if (duration === 0) {
      TweenLite.set(this.$list, {
        x
      });
    } else {
      TweenLite.to(this.$list, duration, {
        x: $itemClone ? cloneX : x,
        onStart: () => {
          this.setAnimating(true);
        },
        onComplete: () => {
          if ($itemClone) {
            TweenLite.set(this.$list, {
              x: this.calcListXForItem($item)
            });
          }

          this.setAnimating(false);
        }
      });
    }
  }

  updatePageNumber(number) {
    if (!this.$pageNumber) {
      return;
    }

    let length = this.$$item.length;

    this.$pageNumber.innerText = `${number}/${length}`;
  }

  toSlide(index, direction = null) {
    if (this.isAnimating()) {
      return;
    }

    index = normalizeIndex(index, this.$$item.length);

    if (index === this.index) {
      return;
    }

    if (this.isCarousel()) {
      this.scrollListToSlide(index, {direction});
    }

    this.$$item[this.index].classList.remove(SliderComponent.CLASSES.ITEM_ACTIVE);
    this.$$item[index].classList.add(SliderComponent.CLASSES.ITEM_ACTIVE);

    if (this.$dots) {
      this.$$dot[this.index].classList.remove(SliderComponent.CLASSES.DOT_ACTIVE);
      this.$$dot[index].classList.add(SliderComponent.CLASSES.DOT_ACTIVE);
    }

    this.updatePageNumber(index + 1);

    this.index = index;
  }

  prevSlide() {
    this.toSlide(this.index - 1, 'prev');
  }

  nextSlide() {
    this.toSlide(this.index + 1, 'next');
  }
}
